import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaTaxi,
  FaCalendarAlt,
  FaRupeeSign,
  FaEye,
  FaSearch,
  FaRoute,
  FaClock,
} from "react-icons/fa";
import { API_URL } from "../config/apiConfig"; // ✅ FIX: use import instead of require
import TripDetailsSummary from "../components/TripDetailsSummary";
import {
  getPaymentMethodDisplay,
  getPaymentStatusDisplay,
  getPaymentMethodBadgeClass,
  getPaymentStatusBadgeClass,
} from "../utils/paymentDisplayUtils";

const BookingHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);

      try {
        const config =
          user && user.token
            ? {
                headers: { Authorization: `Bearer ${user.token}` },
              }
            : {};

        if (user && user.token) {
          try {
            const response = await axios.get(
              `${API_URL}/booking/my-bookings`,
              config,
            );
            if (response.data && response.data.success) {
              setBookings(response.data.data);
              setIsLoading(false);
              return;
            }
          } catch (userBookingError) {
            console.error(
              "User booking fetch failed, falling back to public:",
              userBookingError,
            );
          }
        }

        const publicResponse = await axios.get(`${API_URL}/booking`, config);
        if (publicResponse.data && publicResponse.data.success) {
          setBookings(publicResponse.data.data);
        } else {
          setBookings([]);
          toast.info("No booking history found");
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
        toast.error("Unable to fetch booking history. Please try again later.");
        setBookings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, [user]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }) +
      ", " +
      date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    );
  };

  const getGeneralLocation = (location) => {
    if (!location) return null;

    // Handle string locations
    if (typeof location === "string") {
      return location.trim();
    }

    // Handle object locations
    if (typeof location === "object") {
      // Try to get city/town from various possible fields
      const city =
        location.city ||
        location.town ||
        location.village ||
        location.county ||
        (location.address_components &&
          location.address_components.find((c) => c.types.includes("locality"))
            ?.long_name) ||
        "";

      // Try to get state from various possible fields
      const state =
        location.state ||
        location.region ||
        (location.address_components &&
          location.address_components.find((c) =>
            c.types.includes("administrative_area_level_1"),
          )?.long_name) ||
        "";

      return [city, state].filter(Boolean).join(", ") || null;
    }

    return null;
  };

  // Validate a location string (avoid very short codes or obviously invalid values)
  const isValidLocation = (str) => {
    if (!str || typeof str !== "string") return false;
    const trimmed = str.trim();
    // Too short or placeholder-like strings are considered invalid
    if (trimmed.length < 4) return false;
    const invalidTokens = ["na", "n/a", "da", "af", "d", "a"];
    if (invalidTokens.includes(trimmed.toLowerCase())) return false;
    return true;
  };

  const getSpecificAddress = (location) => {
    if (!location) return null;

    // Handle string locations
    if (typeof location === "string") {
      return location.trim();
    }

    // Handle object locations
    if (typeof location === "object") {
      // Check all possible address fields
      const fields = [
        "address",
        "formatted_address",
        "display_name",
        "name",
        "description",
        "street",
        "road",
        "suburb",
        "neighbourhood",
        "pickupAddress",
        "dropAddress",
      ];

      for (const field of fields) {
        if (location[field] && typeof location[field] === "string") {
          const val = location[field].trim();
          if (val) return val;
        }
      }
    }

    return null;
  };

  const getLocationDisplay = (booking) => {
    // Get URL parameters if they exist
    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get("from");
    const toParam = urlParams.get("to");

    // For pickup location
    let pickup = "";
    let pickupDetails = "";

    // First check database values with proper combination
    if (booking.from && booking.pickupAddress) {
      // If we have both general and detailed address, use them together
      pickup = booking.from;
      pickupDetails = booking.pickupAddress;
    } else if (booking.from) {
      // If only general location exists
      pickup = booking.from;
      pickupDetails = "";
    } else if (fromParam) {
      // If URL parameter exists
      pickup = decodeURIComponent(fromParam);
      pickupDetails = "";
    } else if (
      typeof booking.pickupLocation === "object" &&
      booking.pickupLocation?.name
    ) {
      // If location object exists
      pickup = `${booking.pickupLocation.name}${booking.pickupLocation.state ? `, ${booking.pickupLocation.state}` : ""}`;
      pickupDetails = booking.pickupAddress || "";
    } else if (booking.pickupAddress) {
      // If only detailed address exists
      pickup = booking.pickupAddress;
      pickupDetails = "";
    } else {
      pickup = "Pickup Location";
      pickupDetails = "";
    }

    // For drop location
    let drop = "";
    let dropDetails = "";

    // First check database values with proper combination
    if (booking.to && booking.dropAddress) {
      // If we have both general and detailed address, use them together
      drop = booking.to;
      dropDetails = booking.dropAddress;
    } else if (booking.to) {
      // If only general location exists
      drop = booking.to;
      dropDetails = "";
    } else if (toParam) {
      // If URL parameter exists
      drop = decodeURIComponent(toParam);
      dropDetails = "";
    } else if (
      typeof booking.dropLocation === "object" &&
      booking.dropLocation?.name
    ) {
      // If location object exists
      drop = `${booking.dropLocation.name}${booking.dropLocation.state ? `, ${booking.dropLocation.state}` : ""}`;
      dropDetails = booking.dropAddress || "";
    } else if (booking.dropAddress) {
      // If only detailed address exists
      drop = booking.dropAddress;
      dropDetails = "";
    } else {
      drop = "Drop-off Location";
      dropDetails = "";
    }

    // Clean up any duplicate information
    if (pickupDetails && pickupDetails.includes(pickup)) {
      pickupDetails = pickupDetails.replace(pickup, "").trim();
    }
    if (dropDetails && dropDetails.includes(drop)) {
      dropDetails = dropDetails.replace(drop, "").trim();
    }

    return { pickup, pickupDetails, drop, dropDetails };
  };

  const filteredBookings = bookings
    .filter((booking) => {
      const { pickup, drop } = getLocationDisplay(booking);
      const bookingId = booking.bookingId || booking._id || booking.id || "";
      const cabName =
        typeof booking.cabType === "object" && booking.cabType?.name
          ? booking.cabType.name
          : booking.cabName || "";

      const matchesSearch =
        pickup.toLowerCase().includes(searchTerm.toLowerCase()) ||
        drop.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(bookingId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        cabName.toLowerCase().includes(searchTerm.toLowerCase());

      if (filter === "all") return matchesSearch;

      const bookingDate = new Date(
        booking.pickupDate || booking.travelDate || booking.date || Date.now(),
      );
      const today = new Date();

      return filter === "upcoming"
        ? matchesSearch && bookingDate >= today
        : filter === "completed"
          ? matchesSearch && bookingDate < today
          : matchesSearch;
    })
    .sort((a, b) => {
      // Sort by creation date or booking date in descending order (latest first)
      const dateA = new Date(
        a.createdAt || a.pickupDate || a.travelDate || a.date || 0
      );
      const dateB = new Date(
        b.createdAt || b.pickupDate || b.travelDate || b.date || 0
      );
      return dateB - dateA; // Descending order (latest first)
    });

  const renderBookingCard = (booking) => {
    const id = booking.bookingId || booking._id || booking.id;
    const { pickup, pickupDetails, drop, dropDetails } =
      getLocationDisplay(booking);
    const bookingDate = new Date(
      booking.pickupDate || booking.travelDate || booking.date || Date.now(),
    );
    const formattedDate = bookingDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const pickupTime =
      booking.pickupTime || booking.travelTime || "Time not specified";
    const cabName =
      typeof booking.cabType === "object" && booking.cabType?.name
        ? booking.cabType.name
        : booking.cabName || "Standard Cab";
    const amount = booking.amount || booking.fare || booking.totalAmount || 0;
    const status = booking.status || "pending";
    const paymentMethod = booking.paymentMethod || "cod";

    // Safely parse distance
    const distance = parseFloat(booking.distance || 0);
    const isValidDistance = !isNaN(distance) && distance > 0;

    // Calculate estimated time only if distance is valid
    const avgSpeed = 60; // km/h
    const hours = isValidDistance ? Math.floor(distance / avgSpeed) : 0;
    const minutes = isValidDistance
      ? Math.round((distance % avgSpeed) * (60 / avgSpeed))
      : 0;
    const estimatedTime =
      hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;

    // Create URL parameters with full address details
    const urlParams = new URLSearchParams();
    urlParams.set("from", pickup + (pickupDetails ? `, ${pickupDetails}` : ""));
    urlParams.set("to", drop + (dropDetails ? `, ${dropDetails}` : ""));
    urlParams.set("cabName", cabName);
    urlParams.set("amount", amount);
    if (isValidDistance) urlParams.set("distance", distance);

    return (
      <div
        key={id}
        className="bg-white rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg"
      >
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between">
            <div className="mb-4 md:mb-0">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full mr-4">
                  <FaMapMarkerAlt className="text-blue-600" />
                </div>
                <div>
                  {/* Check if this is a rental service */}
                  {booking.serviceType === "rental" &&
                  (booking.packageName ||
                    booking.rentalHours ||
                    booking.rentalKm) ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
                      <p className="font-semibold text-gray-900 mb-2">
                        {booking.packageName
                          ? `📦 ${booking.packageName}`
                          : "🚗 Rental Service"}
                      </p>
                      <div className="space-y-1 text-sm text-gray-600">
                        {booking.rentalHours && (
                          <p>⏰ Duration: {booking.rentalHours} hours</p>
                        )}
                        {booking.rentalKm && (
                          <p>📏 Included Distance: {booking.rentalKm} km</p>
                        )}
                        <p>📍 Location: {pickup}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Pickup Location */}
                      <div>
                        <p className="font-semibold text-gray-800">{pickup}</p>
                        {pickupDetails && (
                          <p className="text-sm text-gray-600">
                            {pickupDetails}
                          </p>
                        )}
                      </div>

                      {/* Drop-off Location */}
                      <div>
                        <p className="font-semibold text-gray-800">{drop}</p>
                        {dropDetails && (
                          <p className="text-sm text-gray-600">{dropDetails}</p>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-2">
                    Booking ID:{" "}
                    <span className="font-medium">
                      {typeof id === "string" ? id : String(id)}
                    </span>
                  </p>

                  {/* Compact Trip Details Summary - Only show when via cities are present */}
                  {booking.serviceType !== "rental" &&
                    booking.viaCities &&
                    booking.viaCities.length > 0 && (
                      <div className="mt-3">
                        <TripDetailsSummary
                          startLocation={pickup}
                          destination={drop}
                          viaCities={booking.viaCities || []}
                          totalDistance={distance}
                          baseRate={
                            booking.baseRate || booking.pricePerKm || 12
                          }
                          totalFare={amount}
                          tripType={
                            booking.tripType === "roundTrip"
                              ? "Round Trip"
                              : booking.viaCities && booking.viaCities.length > 0
                              ? "Multi-City"
                              : "One Way"
                          }
                          cabType={cabName}
                          compact={true}
                        />
                      </div>
                    )}
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                {/* Remove broken conditional date/time blocks and replace with simple display */}
                <div className="flex items-center mb-2">
                  <span className="mr-2 text-orange-500">📅</span>
                  <span className="font-semibold text-orange-600">
                    {formattedDate}
                  </span>
                </div>
                <div className="flex items-center mb-2">
                  <span className="mr-2 text-red-500">🕒</span>
                  <span className="font-semibold text-red-600">
                    {pickupTime}
                  </span>
                </div>
                <div className="flex items-center">
                  <FaTaxi className="text-gray-500 mr-2" />
                  <span className="text-sm">{cabName}</span>
                </div>
                <div className="flex items-center">
                  <FaRupeeSign className="text-gray-500 mr-2" />
                  <span className="text-sm">₹{amount}</span>
                </div>
                {/* Only show distance for non-rental services */}
                {booking.serviceType !== "rental" && isValidDistance && (
                  <>
                    <div className="flex items-center">
                      <FaRoute className="text-gray-500 mr-2" />
                      <span className="text-sm">
                        {Math.round(distance).toLocaleString("en-IN")} km
                      </span>
                    </div>
                    <div className="flex items-center">
                      <FaClock className="text-gray-500 mr-2" />
                      <span className="text-sm">{estimatedTime}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium text-center ${getPaymentMethodBadgeClass(booking)}`}
              >
                {getPaymentMethodDisplay(booking)}
              </div>
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium text-center ${getPaymentStatusBadgeClass(booking)}`}
              >
                {getPaymentStatusDisplay(booking)}
              </div>
              {/* Show rating status for completed rides */}
              {booking.status === "completed" && (
                <div className="mb-2">
                  {booking.userRating && booking.userRating.rating ? (
                    <div className="flex items-center justify-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      <span>★</span>
                      <span>Rated {booking.userRating.rating}/5</span>
                    </div>
                  ) : (
                    <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full text-center">
                      Not Rated
                    </div>
                  )}
                </div>
              )}
              {/* 🔴 Track Live — only for inProgress */}
              {status === "inProgress" && (
                <a
                  href={`/track/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 flex items-center justify-center gap-2 text-white font-semibold text-sm py-1.5 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    boxShadow: '0 0 10px rgba(99,102,241,0.45)',
                  }}
                >
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: '#10b981', display: 'inline-block',
                    animation: 'pulse 1.5s infinite',
                  }} />
                  Track Live
                </a>
              )}

              <Link
                to={`/booking/${id}?${urlParams.toString()}`}
                className="mt-2 flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                <FaEye /> View Details
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-[1180px] mx-auto px-4 mb-4">
      <div className="mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">My Bookings</h1>
            <p className="text-gray-600 mt-1">
              View and manage all your cab bookings
            </p>
          </div>
          <Link
            to="/"
            className="mt-4 md:mt-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Book a New Cab
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search bookings..."
                className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Bookings</option>
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="rounded-full bg-blue-100 p-3 inline-block mb-4">
              <FaTaxi className="h-8 w-8 text-blue-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              No Bookings Found
            </h2>
            <p className="text-gray-600 mb-6">
              You haven't made any bookings yet or no bookings match your
              search.
            </p>
            <Link
              to="/"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Book Your First Cab
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredBookings.map((booking) => renderBookingCard(booking))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingHistory;
